import React, { useState } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Download } from "lucide-react";
import PageHeader from "./PageHeader";

import useFirebasePreferences from "../hooks/useFirebasePreferences";
import usePreferenceActions from "../hooks/usePreferenceActions";
import { useAuth } from "../helpers/Auth";
import { degreeName, lengthName } from "../helpers/unitHelpers";
import { glasstypes } from "../helpers/glassHelpers";
import { exportUserData } from "../helpers/backupHelpers";

const PreferencePage = () => {
  const { editPreferences } = usePreferenceActions();
  const preferences = useFirebasePreferences();
  const { effectiveUID } = useAuth();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!effectiveUID) return;
    setExporting(true);
    try {
      await exportUserData(effectiveUID);
    } finally {
      setExporting(false);
    }
  };

  const toggleDegrees = () => {
    const newDegrees =
      preferences && preferences.degrees === "fahrenheit" ? "celsius" : "fahrenheit";
    editPreferences({ ...preferences, degrees: newDegrees });
  };

  const toggleLength = () => {
    const newLength = preferences && preferences.length_unit === "in" ? "mm" : "in";
    editPreferences({ ...preferences, length_unit: newLength });
  };

  const setGlassType = (value: string) => {
    editPreferences({ ...preferences, glass_type: value });
  };

  const setProjectListType = (value: "list" | "grid") => {
    editPreferences({ ...preferences, project_list_type: value });
  };

  const setDetailedCard = (checked: boolean) => {
    editPreferences({ ...preferences, detailed_card: checked });
  };

  const setDoNotMail = (checked: boolean) => {
    editPreferences({ ...preferences, do_not_mail: checked });
  };

  const isListView = preferences?.project_list_type === "list";

  return (
    <div className="space-y-6">
      <PageHeader title="User Profile" />
      <ul className="space-y-4">
        <li>
          Temperatures are in:{" "}
          <Button variant="outline" onClick={toggleDegrees}>
            {preferences && degreeName(preferences.degrees)}
          </Button>
        </li>
        <li>
          Lengths are in:{" "}
          <Button variant="outline" onClick={toggleLength}>
            {preferences && lengthName(preferences.length_unit)}
          </Button>
        </li>
        <li className="flex items-center gap-2">
          <span>Default glass type:</span>
          <Select
            value={preferences?.glass_type || "Bullseye"}
            onValueChange={setGlassType}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {glasstypes.map((gt) => (
                <SelectItem key={gt.value} value={gt.value}>
                  {gt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </li>
        <li className="flex items-center gap-2">
          <span>Project list view:</span>
          <div className="inline-flex rounded-md shadow-sm">
            <Button
              variant={isListView ? "outline" : "default"}
              onClick={() => setProjectListType("grid")}
            >
              Cards
            </Button>
            <Button
              variant={isListView ? "default" : "outline"}
              onClick={() => setProjectListType("list")}
            >
              Detail
            </Button>
          </div>
        </li>
        <li className="flex items-center gap-2">
          <Checkbox
            id="detailed_card"
            checked={preferences?.detailed_card ?? false}
            onCheckedChange={(checked) => setDetailedCard(checked === true)}
          />
          <Label htmlFor="detailed_card">Detailed card mode</Label>
        </li>
        <li className="flex items-center gap-2">
          <Checkbox
            id="do_not_mail"
            checked={preferences?.do_not_mail ?? false}
            onCheckedChange={(checked) => setDoNotMail(checked === true)}
          />
          <Label htmlFor="do_not_mail">Do not email me about new features</Label>
        </li>
      </ul>
      <h2 className="text-xl font-bold">Data Export</h2>
      <Button variant="outline" onClick={handleExport} disabled={exporting}>
        <Download className="mr-1 inline-block h-4 w-4" />
        {exporting ? "Exporting..." : "Export All Data"}
      </Button>
      <p className="text-sm text-muted-foreground">
        Downloads a ZIP file containing all your projects, firings, segments, kilns, and inventory data.
      </p>

      <h2 className="text-xl font-bold">Delete User</h2>
      You can delete your user, including all your data, by clicking{" "}
      <a href="/user_delete">Here.</a>
    </div>
  );
};

export default PreferencePage;
